import { useEffect, useRef, useState } from 'react';

/**
 * Iframe Deciplus avec auto-hauteur.
 * - Écoute les messages postMessage émis par Deciplus (si disponibles) pour
 *   ajuster automatiquement la hauteur et supprimer la double barre de défilement.
 * - À défaut, applique une hauteur responsive : plus grande sur mobile (le
 *   contenu s'empile verticalement) pour minimiser le scroll interne.
 */
export function DeciplusIframe({ src, title, defaultHeight = 1000, dataTestId = 'deciplus-iframe' }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(defaultHeight);

  useEffect(() => {
    // Hauteur responsive de départ : mobile plus haut pour éviter le double scroll
    const computeResponsive = () => {
      const isMobile = window.innerWidth < 768;
      setHeight(isMobile ? Math.max(defaultHeight, 1600) : defaultHeight);
    };
    computeResponsive();
    window.addEventListener('resize', computeResponsive);

    // Auto-hauteur si Deciplus émet un message de redimensionnement
    const onMessage = (event) => {
      if (typeof event.origin !== 'string' || !event.origin.includes('deciplus')) return;
      const d = event.data;
      let h = null;
      if (typeof d === 'number') h = d;
      else if (d && typeof d === 'object') {
        h = d.height ?? d?.data?.height ?? d?.iframeHeight ?? null;
      } else if (typeof d === 'string') {
        const m = d.match(/(\d{3,5})/);
        if (m) h = parseInt(m[1], 10);
      }
      if (h && Number.isFinite(h) && h > 300 && h < 6000) {
        window.removeEventListener('resize', computeResponsive);
        setHeight(h);
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('resize', computeResponsive);
      window.removeEventListener('message', onMessage);
    };
  }, [defaultHeight, src]);

  return (
    <iframe
      ref={ref}
      src={src}
      title={title}
      scrolling="no"
      style={{ width: '100%', height: `${height}px`, border: 0, display: 'block' }}
      loading="lazy"
      allow="payment"
      data-testid={dataTestId}
    />
  );
}
